/* [PATCH miniblagajna-fe v1] 05.10.2026 -- Nastavitve za VKR (uvoz izdanih iz FURS Mini blagajne).
   Ista komponenta je:
     - kartica v Nastavitve -> Izdani racuni (ob RacunPlacanKartica),
     - maska ob prvem uvozu (DokumentiPage, odgovor 409 {koda:'vkr_nastavitve'}).
   Bere GET /uvoz-vod/{cid}/vkr-nastavitve, pise PUT (backend preveri konte v kontnem
   planu ter kupca: aktiven in s sifro). Odlocitve Janeza 05.10.: konti, kupec za VKR
   (NE zbirni kupec), "Avtomaticno knjizi placila" + konto placila. */
import React, { useEffect, useState } from 'react';
import { Receipt } from 'lucide-react';
import { KontoInput } from '../KontoInput';
import { PlacnikPartnerVnos } from '../PlacnikPartnerVnos';
import { api } from '../../lib/api';
import toast from 'react-hot-toast';

type VkrN = {
  konto_terjatev: string;
  konto_prihodki: string;
  konto_prihodki_neobd: string;
  kupec_partner_id: string;
  knjizi_placila: boolean;
  konto_placila: string;
};

const PRAZNO: VkrN = {
  konto_terjatev: '', konto_prihodki: '', konto_prihodki_neobd: '',
  kupec_partner_id: '', knjizi_placila: false, konto_placila: '',
};

export function VkrNastavitveKartica({ companyId, maska = false, onShranjeno, onPreklici }: {
  companyId: string;
  maska?: boolean;
  onShranjeno?: () => void;
  onPreklici?: () => void;
}) {
  const [n, setN] = useState<VkrN>(PRAZNO);
  const [kupecNaziv, setKupecNaziv] = useState('');
  const [nalaga, setNalaga] = useState(true);
  const [shranjuje, setShranjuje] = useState(false);
  const [napaka, setNapaka] = useState('');
  const [shranjene, setShranjene] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    setNalaga(true); setNapaka('');
    api.get(`/uvoz-vod/${companyId}/vkr-nastavitve`).then((r: any) => {
      const d = r.data || {};
      const z = d.nastavitve || d.predlog || {};
      setN({
        konto_terjatev: z.konto_terjatev || '',
        konto_prihodki: z.konto_prihodki || '',
        konto_prihodki_neobd: z.konto_prihodki_neobd || '',
        kupec_partner_id: z.kupec_partner_id || '',
        knjizi_placila: !!z.knjizi_placila,
        konto_placila: z.konto_placila || '',
      });
      setKupecNaziv((d.kupec && d.kupec.naziv) || '');
      setShranjene(!!d.nastavitve);
    }).catch(() => setNapaka('Nastavitev za VKR ni bilo mogoče prebrati.'))
      .finally(() => setNalaga(false));
  }, [companyId]);

  const nastavi = (k: keyof VkrN, v: any) => setN(prej => ({ ...prej, [k]: v }));

  const shrani = async () => {
    setShranjuje(true); setNapaka('');
    try {
      await api.put(`/uvoz-vod/${companyId}/vkr-nastavitve`, n);
      setShranjene(true);
      toast.success('Nastavitve za VKR so shranjene');
      if (onShranjeno) onShranjeno();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setNapaka(typeof d === 'string' ? d : 'Shranjevanje ni uspelo.');
    } finally { setShranjuje(false); }
  };

  const oznaka = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center">
          <Receipt className="w-5 h-5" />
        </div>
        <div>
          <h2 className="font-semibold text-gray-900 dark:text-white">
            {maska ? 'Pred prvim uvozom iz Mini blagajne — Nastavitve za VKR' : 'Nastavitve za VKR — uvoz iz Mini blagajne'}
          </h2>
          <p className="text-xs text-gray-500">
            Uvoz Excel izvoza FURS Mini blagajne (Izdani → Uvoz). Ime datoteke se začne z davčno številko podjetja.
            DDV konti se vzamejo iz Kontiranja izdanih računov.
            {!shranjene && !nalaga && <span className="text-amber-600"> Nastavitve še niso shranjene.</span>}
          </p>
        </div>
      </div>

      {nalaga ? (
        <div className="mt-4 text-sm text-gray-500">Nalagam…</div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={oznaka}>Konto terjatev do kupcev *</label>
              <KontoInput value={n.konto_terjatev} placeholder="120000"
                onChange={(konto: string) => nastavi('konto_terjatev', konto)} />
            </div>
            <div>
              <label className={oznaka}>Kupec za VKR račune * (partner s šifro)</label>
              <PlacnikPartnerVnos companyId={companyId}
                partnerId={n.kupec_partner_id} partnerNaziv={kupecNaziv}
                onIzbira={(partner_id: string, partner_naziv: string) => {
                  nastavi('kupec_partner_id', partner_id || ''); setKupecNaziv(partner_naziv || '');
                }} />
            </div>
            <div>
              <label className={oznaka}>Konto prihodkov *</label>
              <KontoInput value={n.konto_prihodki} placeholder="760000"
                onChange={(konto: string) => nastavi('konto_prihodki', konto)} />
            </div>
            <div>
              <label className={oznaka}>Konto prihodkov — neobdavčen promet (prazno = isti)</label>
              <KontoInput value={n.konto_prihodki_neobd} placeholder={n.konto_prihodki || '760000'}
                onChange={(konto: string) => nastavi('konto_prihodki_neobd', konto)} />
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={n.knjizi_placila}
                onChange={e => nastavi('knjizi_placila', e.target.checked)} />
              Avtomatično knjiži plačila
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Plačani znesek (stolpec »Rac plac«) se knjiži v breme spodnjega konta in v dobro terjatve; račun se označi kot plačan.
            </p>
            {n.knjizi_placila && (
              <div className="mt-3 sm:w-1/2">
                <label className={oznaka}>Konto plačila *</label>
                <KontoInput value={n.konto_placila} placeholder="100000"
                  onChange={(konto: string) => nastavi('konto_placila', konto)} />
              </div>
            )}
          </div>

          {napaka && (
            <div className="mt-4 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm p-3">{napaka}</div>
          )}

          <div className="mt-4 flex justify-end gap-2">
            {onPreklici && (
              <button onClick={onPreklici} disabled={shranjuje}
                className="px-4 py-2 rounded-xl text-sm border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-40">
                Prekliči
              </button>
            )}
            <button onClick={shrani} disabled={shranjuje}
              className="px-4 py-2 rounded-xl text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40">
              {shranjuje ? 'Shranjujem…' : (maska ? 'Shrani in nadaljuj z uvozom' : 'Shrani')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
